import { createClient } from '@blinkdotnew/sdk';
import { requestManager } from '../lib/requestManager';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
});

export interface UserModel {
  id: string;
  userId: string;
  modelStatus: string;
  confidenceScore: number;
  totalEmailsAnalyzed: number;
  personalityTraits?: string;
  workStyle?: string;
  communicationPreferences?: string;
  priorities?: string;
  decisionMakingStyle?: string;
  behavioralSummary?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmailStatistics {
  id: string;
  userId: string;
  statDate: string;
  totalReceived: number;
  totalSent: number;
  totalUrgent: number;
  avgResponseTimeHours: number;
  topContacts?: string;
  busiestHours?: string;
  communicationTrends?: string;
  createdAt: string;
}

export interface Contact {
  id: string;
  userId: string;
  email: string;
  name?: string;
  company?: string;
  relationshipType?: string;
  relationshipStage?: string;
  relationshipHealth?: string;
  engagementLevel?: string;
  trustLevel?: string;
  communicationStyle?: string;
  powerDynamic?: string;
  aiRelationshipScore: number;
  totalInteractions: number;
  lastInteractionAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const userModelService = {
  /**
   * Check if user has an existing model
   */
  async hasUserModel(userId: string): Promise<boolean> {
    try {
      const models = await requestManager.execute(
        `user_model_exists:${userId}`,
        () => blink.db.userModels.list({
          where: { userId },
          limit: 1,
        }),
        {
          priority: 5,
          cacheTTL: 120000
        }
      );
      return models.length > 0;
    } catch (error) {
      console.error('Error checking user model:', error);
      return false;
    }
  },

  /**
   * Get user model
   */
  async getUserModel(userId: string): Promise<UserModel | null> {
    try {
      const models = await requestManager.execute(
        `user_model:${userId}`,
        () => blink.db.userModels.list({
          where: { userId },
          orderBy: { createdAt: 'desc' },
          limit: 1,
        }),
        {
          priority: 8,
          cacheTTL: 120000
        }
      );
      return models.length > 0 ? (models[0] as UserModel) : null;
    } catch (error) {
      console.error('Error getting user model:', error);
      return null;
    }
  },

  /**
   * Create user model by calling the edge function
   */
  async createUserModel(userId: string): Promise<{ success: boolean; message: string; stats?: any }> {
    try {
      // Call the edge function to process emails and create the model
      const response = await fetch(
        'https://lemon-ui-agentic-email-ai-agent-ben3ou87.functions.blink.new/create-user-model',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${blink.auth.isAuthenticated() ? await getAuthToken() : ''}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create user model: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating user model:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create user model',
      };
    }
  },

  /**
   * Get email statistics for user
   */
  async getEmailStatistics(userId: string): Promise<EmailStatistics | null> {
    try {
      const stats = await requestManager.execute(
        `email_stats:${userId}`,
        () => blink.db.emailStatistics.list({
          where: { userId },
          orderBy: { createdAt: 'desc' },
          limit: 1,
        }),
        {
          priority: 5,
          cacheTTL: 300000
        }
      );
      return stats.length > 0 ? (stats[0] as EmailStatistics) : null;
    } catch (error) {
      console.error('Error getting email statistics:', error);
      return null;
    }
  },

  /**
   * Get all contacts for user
   */
  async getContacts(userId: string): Promise<Contact[]> {
    try {
      const contacts = await requestManager.execute(
        `contacts:${userId}:top50`,
        () => blink.db.contacts.list({
          where: { userId },
          orderBy: { aiRelationshipScore: 'desc' },
          limit: 50,
        }),
        {
          priority: 5,
          cacheTTL: 120000
        }
      );
      return contacts as Contact[];
    } catch (error) {
      console.error('Error getting contacts:', error);
      return [];
    }
  },

  /**
   * Get top contacts by relationship score
   */
  async getTopContacts(userId: string, limit: number = 5): Promise<Contact[]> {
    try {
      const contacts = await requestManager.execute(
        `contacts:${userId}:top${limit}`,
        () => blink.db.contacts.list({
          where: { userId },
          orderBy: { aiRelationshipScore: 'desc' },
          limit,
        }),
        {
          priority: 8,
          cacheTTL: 60000
        }
      );
      return contacts as Contact[];
    } catch (error) {
      console.error('Error getting top contacts:', error);
      return [];
    }
  },

  /**
   * Parse JSON fields from model
   */
  parseModelField(field?: string): any {
    if (!field) return null;
    try {
      return safeJsonParse(field);
    } catch {
      return field;
    }
  },
};

async function getAuthToken(): Promise<string> {
  // Get the current auth token from Blink SDK
  const user = await blink.auth.me();
  return user ? 'user-token' : '';
}
